/**
 * Request team and space membership for Request Correspondence (#1069).
 *
 * Uses the REAL team attached to the request (`GET /requests/:id/team`,
 * `TeamInformation[]`) rather than invented personas.
 *
 * MEMBERSHIP IS AUTOMATIC. Everyone on the request team belongs to every thread
 * they are entitled to see. There is no per-thread invitation, and no UI for
 * adding someone to a thread — thread membership is a projection of request
 * membership, not a separate thing to maintain.
 *
 *   shared space   = the whole request team, internal and external
 *   internal space = the internal members only
 *
 * Internal vs external comes from the team ROLE (cycle 6), never from an email
 * domain and never from `userType`, which is recomputed from the domain on
 * every login and drifts from roles an administrator has since changed.
 *
 * ⚠️ THIS FILE DECIDES NOTHING. The server computes membership itself from
 * `team_information` and `users.user_id` and refuses regardless of what the
 * panel believes. Everything here is display: who to show in the strip, and
 * whether a message is "mine".
 *
 * ⚠️ NO IDENTIFIER IS DERIVED FROM AN EMAIL (Story 1069-1.1, AR3). The old
 * `providerUserId` sanitised an address into a messaging id; it is gone. The
 * viewer's `id` is the numeric `users.user_id` the session already holds, as a
 * string, which is exactly what `Message.authorId` carries on the wire. A
 * team row's `id` is a client-local key (the lower-cased email) that never
 * reaches the server and never matches an `authorId`.
 */

import { AppRole } from '../../types/auth';
import { getVertivEmailVariants } from '../../utils/emailNormalization';

import type { Message, SpaceId } from './correspondence.types';
import type { TeamInformation } from '../../types';

export type TeamRole = TeamInformation['userRole'];

/**
 * Team roles that positively establish somebody as Vertiv-internal.
 *
 * ⚠️ MIRRORS `server/services/internalTeamRole.ts`, WHICH IS THE AUTHORITY.
 * This copy is DISPLAY ONLY and decides nothing: the server computes space
 * membership itself and refuses regardless of what the panel believes. The two
 * are a mirror rather than one shared module because `server/tsconfig.json` sets
 * `rootDir: "./"` and so cannot compile an import of anything under `src/` —
 * `server/services/__tests__/internalTeamRole.sync.test.ts` imports both and
 * fails the build if they disagree. **Edit one and you must edit the other.**
 */
const INTERNAL_TEAM_ROLES: string[] = [
  'vertiv_sponsor',
  'vertiv_contributor',
  'default_sponsor_nam',
  'default_sponsor_emea',
  'default_sponsor_apac',
];

/**
 * A `Map`, NOT an object literal (correspondence.md, "Stored is served"):
 * `userRole` is an API-supplied string and `team-information.route.ts` writes
 * it with no allowlist, so an object literal keyed by it resolves `'constructor'`
 * on `Object.prototype` and the `?? 'Team member'` fallback never fires.
 */
export const TEAM_ROLE_LABEL: ReadonlyMap<string, string> = new Map<
  TeamRole,
  string
>([
  ['vertiv_sponsor', 'Sponsor'],
  ['vertiv_contributor', 'Contributor'],
  ['partner_contributor', 'Partner'],
]);

export interface SpaceMember {
  /**
   * A client-local key. For the VIEWER it is the numeric user id as a string
   * (`User.id`), which is what `Message.authorId` carries on the wire. For a
   * TEAM ROW it is the lower-cased email, because the team endpoint carries
   * no user id; it never reaches the server and never matches an `authorId`.
   *
   * ⚠️ Author display names are therefore NOT derivable on the client for
   * anyone but the viewer. Story 1.2's read endpoints supply them.
   */
  id: string;
  /**
   * Empty for a NON-MEMBER AUTHOR (`withNonMemberAuthors`): a message carries
   * no address, and none is added to the wire for the strip's sake. The strip
   * draws no email line for an empty one.
   */
  email: string;
  name: string;
  /**
   * What the strip prints beside the name. A team row's TEAM role
   * (`TEAM_ROLE_LABEL`), "Requestor" for the requestor fold, and for a
   * non-member author whatever the SERVER said (`Message.authorNonMemberRole`).
   * The client derives no label from app roles: the one person it could
   * (the viewer) is listed the same way as everybody else, through their own
   * messages.
   */
  roleLabel: string;
  isInternal: boolean;
}

/**
 * Do two addresses name the same person? Case-insensitive, and the two Vertiv
 * domains are one (see `utils/emailNormalization.ts`).
 *
 * ⚠️ DISPLAY DE-DUPLICATION ONLY. No identifier is derived from this; it only
 * stops the strip showing one human twice.
 */
export const sameEmail = (
  a: string | undefined,
  b: string | undefined,
): boolean => {
  if (!a || !b) return false;
  const variants = new Set(getVertivEmailVariants(a));
  return getVertivEmailVariants(b).some(v => variants.has(v));
};

/**
 * FAILS CLOSED, AND THE VERSION THIS REPLACED FAILED OPEN.
 *
 * It was `!['partner_contributor'].includes(role)`, so ANY value that was not
 * literally that string resolved to INTERNAL — the same shape the tombstone at
 * the bottom of this file records as deleted for being dangerous. It was not
 * hypothetical: migration 058 widened `team_information.user_role` to six values
 * and nothing here was touched, so three roles have been classified correctly by
 * luck rather than by decision.
 *
 * Takes `unknown` rather than `TeamRole` because `src/types/index.ts` declares
 * three of the six real values, so the declared type cannot be trusted to
 * constrain what actually arrives from the API.
 */
export const isInternalTeamRole = (role: unknown): boolean =>
  typeof role === 'string' && INTERNAL_TEAM_ROLES.includes(role);

/**
 * App roles that make a person external. Mirrors `getDefaultRoles`, which
 * assigns External Requestor and Partner Contributor together as the external
 * pair (cycle 6).
 */
const EXTERNAL_APP_ROLES: string[] = [
  // ⚠️ THE TWO `LVO` ROLES ARRIVED WITH #1475 IN THE 2026-09-14 MERGE OF
  // `development`, and they belong HERE, on the external side. LVO is a
  // partner programme: these are customer-side people.
  //
  // This list is a VETO, not a hint — it wins over any internal role a person
  // also holds (see the `Partner Contributor` + `IT Administrator` note
  // below). So leaving the LVO pair out was not merely untidy: somebody
  // carrying `Partner Contributor LVO` alongside an internal role would have
  // classified as Vertiv-internal and been shown internal conversations.
  // `internalIdentity.test.ts`'s every-role-in-exactly-one-bucket guard is
  // what caught it the moment the roles landed.
  AppRole.EXTERNAL_REQUESTOR,
  AppRole.EXTERNAL_REQUESTOR_LVO,
  AppRole.PARTNER_CONTRIBUTOR,
  AppRole.PARTNER_CONTRIBUTOR_LVO,
];

/**
 * Internal vs external for the SIGNED-IN (or impersonated) user, from their own
 * app roles.
 *
 * Deliberately not a lookup against the request team. The person on screen may
 * legitimately not appear in `team_information` — an external requestor who
 * created the request is the obvious case — and a lookup miss must never be
 * allowed to imply anything about privilege.
 *
 * FAILS CLOSED: no roles, or roles we do not recognise, resolve to external,
 * which sees less.
 *
 * WHERE THE ROLES COME FROM, and why this is the whole answer (settled
 * 2026-08-17, product owner):
 *
 * `server/services/role-assignment.service.ts` stamps roles at FIRST LOGIN from
 * the email domain — `vertiv.com` / `vertivco.com` / `lab.vertivco.com` get
 * `Requestor` + `Vertiv Contributor`; everyone else gets `External Requestor` +
 * `Partner Contributor`.
 *
 * ⚠️ `[CHANGED 2026-09-21, Story 8.1]` This used to read "the lists below
 * mirror both sides of that split". Only ONE side is a list now:
 * `EXTERNAL_APP_ROLES` is hand-kept and `INTERNAL_APP_ROLES` is its complement
 * over `AppRole`, per the owner's ruling that the only external roles are the
 * partner and external-requestor ones. The external list is still a VETO that
 * wins over any internal role held beside it.
 *
 * **A Vertiv email address means internal, always, by design.** A contractor
 * holding a Vertiv mailbox is internal, and that is the intended outcome rather
 * than a gap to close. An admin editing somebody's roles to cross the boundary
 * is not a scenario worth engineering against.
 *
 * This was raised twice as a possible confidentiality hole and closed twice.
 * **Do not reopen it without new information** — and do not add a second
 * classifier "just for this case".
 *
 * ⚠️ THE AUTHORITATIVE COPY IS NOW `server/services/internalIdentity.ts`
 * (#1069 phase 2). THIS ONE IS DISPLAY ONLY and decides nothing: the server
 * refuses an internal-space write regardless of what the panel believes. The
 * two are a MIRROR rather than one shared module because `server/tsconfig.json`
 * sets `rootDir: "./"` and so cannot compile an import of anything under `src/`
 * — `server/services/__tests__/internalIdentity.sync.test.ts` imports both and
 * fails the build if they disagree. **Edit one and you must edit the other.**
 */
export const isInternalByAppRoles = (roles: string[] | undefined): boolean => {
  if (!roles || roles.length === 0) return false;
  if (roles.some(r => EXTERNAL_APP_ROLES.includes(r))) return false;
  return roles.some(r => INTERNAL_APP_ROLES.includes(r));
};

/**
 * Roles that positively establish somebody as Vertiv-internal.
 *
 * ⚠️ DERIVED, NOT HAND-KEPT, SINCE 2026-09-21 (Story 8.1). External is the one
 * list anybody maintains; internal is its complement over `AppRole`, so a role
 * added to the enum tomorrow is internal by default. The mirror in
 * `server/services/internalIdentity.ts` carries the identical expression over
 * the server's own `AppRole`, and `internalIdentity.sync.test.ts` holds both to
 * one table of literal answers.
 *
 * ⚠️ Declared BELOW `isInternalByAppRoles`, which reads it at call time, so the
 * `const` TDZ is never hit. Keep that order or move the list above the
 * function; do not leave a module-scope read between them.
 *
 * ⚠️ The order here is this enum's order, which differs from the server enum's.
 * Nothing consumes it (every read is `.includes`), and this copy is not
 * exported: it is pinned by BEHAVIOUR through the sync test's case table, not
 * by a list comparison.
 */
const INTERNAL_APP_ROLES: string[] = Object.values(AppRole).filter(
  r => !EXTERNAL_APP_ROLES.includes(r),
);

/**
 * The person actually using the panel, built from their own identity rather
 * than from a team lookup. `teamRole` is best-effort and only used for display.
 *
 * `id` is `User.id`: the numeric `users.user_id` as a string, which the server
 * sends as `Message.authorId`. Without an id there is no viewer, because
 * nothing could ever be "mine"; without an email there is no viewer either,
 * because the strip has nothing to show.
 */
export const viewerFromUser = (
  id: string | undefined,
  email: string | undefined,
  name: string | undefined,
  roles: string[] | undefined,
): SpaceMember | null => {
  if (!id || !email) return null;
  // ⚠️ THE VIEWER ID MUST BE THE NUMERIC `users.user_id`, AS A STRING. It is
  // compared with `Message.authorId`, which is that number on the wire. The
  // server sends the database id as `id` on every auth path, but an older
  // build (or a session that predates that fix) can still send an Okta subject
  // ('00u...'), and a silent mismatch means NO message is ever recognised as
  // the viewer's own, with nothing in the UI to say why. Refuse it instead:
  // the panel then behaves as it does for any unidentified viewer rather than
  // rendering confidently wrong attribution.
  if (!/^\d+$/.test(id)) return null;
  const isInternal = isInternalByAppRoles(roles);
  return {
    id,
    email,
    name: name?.trim() || email,
    // Never listed since 2026-10-06 (no viewer fold), so derived from nothing.
    roleLabel: '',
    isInternal,
  };
};

export const toSpaceMember = (t: TeamInformation): SpaceMember => ({
  id: t.userEmail.trim().toLowerCase(),
  email: t.userEmail,
  name: t.userFullName?.trim() || t.userEmail,
  // `userRole` is typed as three values and the API can send six (see
  // `isInternalTeamRole`); an unlabelled one prints as a plain team member
  // rather than `undefined`.
  roleLabel: TEAM_ROLE_LABEL.get(t.userRole) ?? 'Team member',
  isInternal: isInternalTeamRole(t.userRole),
});

export const toSpaceMembers = (team: TeamInformation[]): SpaceMember[] => {
  // The same person can appear more than once if they hold two roles on a
  // request, or under both Vertiv domains; collapse to the most privileged
  // (internal wins).
  const out: SpaceMember[] = [];
  for (const t of team) {
    const m = toSpaceMember(t);
    const existing = out.find(e => sameEmail(e.email, m.email));
    if (!existing) {
      out.push(m);
    } else if (m.isInternal && !existing.isInternal) {
      out.splice(out.indexOf(existing), 1, m);
    }
  }
  return out;
};

/**
 * Who belongs to a space. Note the asymmetry, and that it is deliberate: there
 * is no team role that puts an external person into the internal space.
 */
export const membersForSpace = (
  members: SpaceMember[],
  space: SpaceId,
): SpaceMember[] =>
  space === 'shared' ? members : members.filter(m => m.isInternal);

export const memberIdsForSpace = (
  members: SpaceMember[],
  space: SpaceId,
): string[] => membersForSpace(members, space).map(m => m.id);

/**
 * Fold the request's requestor into membership.
 *
 * The requestor is NOT a `team_information` row — they created the request, they
 * are not a sponsor or contributor on it. So without this they are absent from
 * space membership entirely, and a shared thread created by anyone else is
 * invisible to the very customer it was written for. That is the mirror of the
 * creator-only-member failure: same root cause, opposite direction.
 *
 * Added as EXTERNAL, i.e. shared space only, unless they already appear in
 * `team_information` with a role of their own — in which case that row wins,
 * because it is the authoritative statement of their standing on this request.
 * Guessing INTERNAL here would hand the internal space to somebody on the basis
 * of an email alone.
 *
 * ⚠️ ONE EXCEPTION, AND IT IS NOT A GUESS (2026-09-21, #1069 Story 8.2). When
 * the VIEWER is the requestor, the client is not inferring anything: it holds
 * that person's own app roles, already classified by `isInternalByAppRoles` in
 * `viewerFromUser`. An internal Vertiv person who created a request and has
 * added nobody to it has no `team_information` row, so the old fold wrote them
 * into their own request as external and the strip under-counted the internal
 * space they were standing in. Their standing is their own, so it is taken from
 * them rather than from their address.
 *
 * ⚠️ AND ONLY FOR THE VIEWER. A THIRD-PARTY internal requestor with no team row
 * still folds in as external, so an internal viewer can read "2 people" where
 * the server admits 3. That is a RECORDED DEFERRAL (Story 8.2, C5), not an
 * oversight: the client holds `requestorEmail` and `requestorName` and nothing
 * else about that person, and no membership count crosses the wire. Fixing it
 * honestly needs a server-supplied signal, which is an API-surface decision.
 * Never guess it here.
 */
export const withRequestor = (
  members: SpaceMember[],
  requestorEmail: string | undefined,
  requestorName: string | undefined,
  viewer?: SpaceMember | null,
): SpaceMember[] => {
  if (!requestorEmail) return members;
  if (members.some(m => sameEmail(m.email, requestorEmail))) return members;
  // The only person the client may classify is the signed-in viewer.
  const viewerIsRequestor = !!viewer && sameEmail(viewer.email, requestorEmail);
  const isInternal = viewerIsRequestor ? viewer.isInternal : false;
  return [
    ...members,
    {
      id: requestorEmail.trim().toLowerCase(),
      email: requestorEmail,
      name: requestorName?.trim() || requestorEmail,
      // Their standing on the request, not a guessed team role: they created
      // it. (Until 2026-10-06 this printed "Partner" or "Contributor".)
      roleLabel: 'Requestor',
      isInternal,
    },
  ];
};

/**
 * Fold the OPEN conversation's non-member authors into the strip (2026-10-06).
 *
 * A person who reads through a parent app role (IT Administrator, Supervisor,
 * Business Owner, ...) has no `team_information` row and is not the requestor,
 * so the server's projection does not count them and neither does this list.
 * They appear only once they have SPOKEN in the conversation that is open, and
 * then under the label the server put on their message
 * (`authorNonMemberRole`, `correspondenceAuthorRole.ts`). Nothing posted means
 * nothing listed, the viewer included.
 *
 * ⚠️ WHY THE SERVER SAYS WHO IS OUTSIDE. Team rows are keyed here by email and
 * messages by `users.user_id`, so the client cannot match an author to a team
 * row; a member author carries no field and is listed by their team row alone.
 * Keyed by `authorId`, so several messages by one person are one row.
 *
 * Known limit: an author whose only message sits beyond the loaded page, or in
 * another conversation of the space, is not listed until it is loaded. The
 * count moves when a conversation opens; that is who has spoken, not a guess.
 */
export const withNonMemberAuthors = (
  members: SpaceMember[],
  messages: readonly Pick<
    Message,
    'authorId' | 'authorName' | 'authorIsInternal' | 'authorNonMemberRole'
  >[],
): SpaceMember[] => {
  const out = [...members];
  const seen = new Set<string>();
  for (const m of messages) {
    if (m.authorNonMemberRole === undefined || seen.has(m.authorId)) continue;
    seen.add(m.authorId);
    out.push({
      id: m.authorId,
      email: '',
      name: m.authorName,
      roleLabel: m.authorNonMemberRole,
      isInternal: m.authorIsInternal,
    });
  }
  return out;
};

/**
 * May this viewer withdraw ANYBODY's message (Story 1069-1.3, AC 35)? IT
 * Administrator only. DISPLAY CONVENIENCE: it decides whether the row OFFERS
 * "Delete" on other people's messages; the server is the control (NFR8) and
 * refuses regardless. Same fail-closed shape as `isInternalByAppRoles`: an
 * array only, no external role present, and a positive match.
 */
export const mayDeleteAnyMessage = (roles: string[] | undefined): boolean => {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  if (roles.some(r => EXTERNAL_APP_ROLES.includes(r))) return false;
  return roles.includes(AppRole.IT_ADMINISTRATOR);
};

export const canSeeSpace = (
  members: SpaceMember[],
  userId: string,
  space: SpaceId,
): boolean => memberIdsForSpace(members, space).includes(userId);

export const findMember = (
  members: SpaceMember[],
  userId: string,
): SpaceMember | undefined => members.find(m => m.id === userId);

/*
 * REMOVED 2026-08-17: `isInternalByRoles` and its `EXTERNAL_ROLES` list.
 *
 * It had no callers, and its doc comment claimed "fail closed" while the
 * implementation did the opposite: `roles.length > 0 && !roles.some(isExternal)`
 * returns TRUE for any role on neither list, so an unrecognised role resolved to
 * INTERNAL and would have been shown the internal space.
 *
 * Deleted rather than fixed because `isInternalByAppRoles` above is the live
 * classifier, genuinely fails closed (it requires a positive match against
 * INTERNAL_APP_ROLES), and is covered by tests. Two classifiers in one file with
 * opposite failure modes is a trap for whoever reads the reassuring comment
 * first.
 *
 * REMOVED 2026-09-04 (Story 1069-1.1): `providerUserId` and `canonicalEmail`.
 * They derived a messaging id from an email. Every identity is `users.user_id`
 * now; the server's `noEmailDerivedIdentity.test.ts` guards the server side.
 *
 * REMOVED 2026-10-06: `withViewer`. It appended the signed-in person to the
 * strip whenever they had no team row, under a GUESSED team label
 * ("Contributor" for any internal role), so an IT Administrator or Supervisor
 * who merely opened the panel was listed and counted as a team member, and the
 * count disagreed with the server's projection. The viewer is now listed like
 * anyone else: by a team row, as the requestor, or through their own messages
 * (`withNonMemberAuthors`). `SpaceMember.role` went with it; the strip prints
 * `roleLabel`.
 */
